import type { LoaderFunctionArgs } from '~/types/route-args';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import { useEffect, useRef, type ComponentProps } from 'react';
import { redirect } from 'react-router';
import { useLoaderData, useLocation, useSearchParams } from 'react-router';
import { mergeMeta } from '~/modules/seo';
import type { AvatarProps } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import {
    useAppDisplayModeContext,
    Container,
    Box,
} from '@archipro-design/aria';
import { getDirectoryPageMeta } from '@modules/category/api';
import { getDirectoryPageMetaCombined } from '~/modules/category/api/get-directory-page-meta-combined.server';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import {
    querySearchServiceAggregate,
    querySearchServiceSearch,
} from '~/modules/search';
import { mapESProfessionalTileViewModel } from '@modules/search/util/elasticsearch-professional-tile-view';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import {
    createEmptyFilterStates,
    createFilterStates,
} from '~/modules/search/util/create-filter-states';
import { getProfessionalsCategories } from '@modules/professionals-category/api';
import AdvertBannerTile from '@modules/adverts/component/advert-banner-tile/AdvertBannerTile';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import {
    getProfessionalsLocation,
    getProfessionalCategoriesInLocation,
} from '~/modules/seo/api/get-page-localisation.server';
import { logDirectoryItemClick } from '~/modules/category/util/log-directory-item-click';
import { useTracker } from '@archipro-website/tracker';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '~/modules/search/hook/use-search-filters';
import type { SearchLocation } from '~/modules/search/type/api';
import { buildLocationOverrideParams } from '@modules/search/util/filter-url-helpers';
import { toSearchCoordinate } from '~/modules/search/util/search-coordinate';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import type { ProfessionalTileViewModel } from '~/modules/professional/type';
import { getSession } from '~/modules/root/util/session.server';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { shuffleArray } from '~/utils/arrayHelper';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { checkTeamMemberInvitation } from '~/modules/professionals-category/util/check-team-member-invitation.server';
import { adRequest, adsRequest } from '@modules/adverts/api';
import { advertCreativeProfessionalTileView } from '@modules/professionals-category/util/advert-creative-professional-tile-view';
import ProfessionalAdvertTile from '@modules/tile/component/professional-tile/ProfessionalAdvertTile';
import { AdvertProjectPlacementTile } from '~/modules/adverts/component/advert-project-placement/AdvertProjectPlacementTile';
import type { ProjectAd } from '~/modules/adverts/type';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { parseURL } from '@archipro-website/top-navigation';
import {
    hasFeature,
    useFeature,
    useTileHotButtons,
    type APHandle,
} from '~/modules/root';
import { useUser } from '~/modules/user';
import APConnectBanner from '~/modules/category/component/ap-connect-banner/APConnectBanner';
import * as S from '~/modules/professionals-category/components/professional-category-container/ProfessionalCategoryContainer.style';
import getShowProfessionalArchiproConnectBanner from '~/utils/get-show-professional-archipro-connect-banner';
import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import type { ProfessionalTileViewModel as ProfessionalTileViewModelRocco } from '@rocco/ui/tile-professional/view-models/professional';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { getProfessionalsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-professional';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { mapProfessionalToTileViewModel } from '~/modules-rocco/professionals/data-mappers/tile-professional';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import { response404 } from '@modules/root/graphql/assertResponse.server';
import { B2BLandingPage } from '@modules/b2b/B2BLandingPage';
import { DirectoryBrowsePage } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import { DirectoryPillBar } from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';
import { useDirectoryDisplayColumns } from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { DirectoryFilterSidebar } from '~/ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import { DirectoryBrowseGrid } from '~/ui-rocco/pages/directory-browse/DirectoryBrowseGrid';
import { DirectoryIntro } from '~/ui-rocco/pages/directory-browse/DirectoryIntro';
import { GetMatchedModal } from '~/ui-rocco/pages/category-page/GetMatchedModal';
import {
    getMatchedStyleImages as fetchGetMatchedStyleImages,
    type GetMatchedStyleImage,
} from '~/modules/get-matched/api/get-matched-style-images.server';
import { DirectoryCategoryCarousel } from '~/ui-rocco/pages/directory-browse/DirectoryCategoryCarousel';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import { isCategoryRevampActive } from '@modules/category/util/is-category-revamp-active.server';
import {
    loadDirectoryBrowse,
    type DirectoryBrowseLoaderData,
} from '@modules/category/util/load-directory-browse.server';
import { PROFESSIONAL_FILTERS_CONFIG } from '@modules-rocco/professionals/config/filter.config';
import { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { profsConfig } from '@modules/b2b/config/profs.config';
import {
    collectPartnerUrlSegments,
    getB2BPartnerBanners,
} from '@modules/b2b/api/get-b2b-partner-banners.server';
import {
    buildB2BRootLoaderData,
    isB2BRootLoaderData,
} from '~/utils/business-subdomain';
import { getHost, isBusinessHost } from '~/utils/request.server';
import CategoryPageLocations from '@ui-rocco/pages/category-page/CategoryPageLocations';
import CategoryPageCategoriesInLocation from '@ui-rocco/pages/category-page/CategoryPageCategoriesInLocation';
import { buildLocationMeshData } from '~/modules/professionals-category/util/location-breadcrumb';
import { buildProfessionalDirectoryDisplayTitle } from '~/modules/professionals-category/util/location-meta';
import { buildLocationPathRedirect } from '~/modules/professionals-category/util/location-redirect';
import { resolveProfessionalsLocationMeta } from '~/modules/professionals-category/util/resolve-location-meta.server';
import { buildProfessionalsMetaDescriptors } from '~/modules/professionals-category/util/professionals-meta';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { FollowSelectorProxy } from '~/modules/moodboards/components';
import { getProfessionalCategories as getProfessionalCategoriesRocco } from '~/modules-rocco/professionals/api/get-professional-categories.server';
import { parseURL as parseURLRocco } from '@modules-rocco/url/api/parse-url';
import { mapCategoriesToTileViewModel } from '@modules-rocco/categories/data-mappers/category-to-tile';
import ProfessionalCategoryExploreSection from '@ui-rocco/pages/category-page/ProfessionalCategoryExploreSection';
import { buildContextualProfessionalCategories } from '~/modules/professionals-category/util/build-contextual-professional-categories';
import { parseFooterContentToFAQ } from '~/modules/professionals-category/util/parse-footer-content-to-faq.server';
import { TileViewTransitionScope } from '@rocco/contexts/TileViewTransitionScopeContext';

export const meta = mergeMeta<typeof loader>(
    // This function overrides meta exported from root
    () => [],
    buildProfessionalsMetaDescriptors,
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
    // Big-A unified header (BC-4480) — matches the approved directory mock.
    header: { showLogomark: true },
};

const DEFAULT_PROFESSIONAL_PAGE_SIZE = 36;
const DEFAULT_SORT = 'trending';
// insert project placement ads every 2 rows.
const ADS_SLOT_GAP_MOBILE = 2; // 1 tiles per row for mobile
const MAX_ADS_SLOT = 3;

const MAX_SPONSORED_AD_TILES_DISPLAYED = 3;

/** Consumer professionals directory data fetch (apex host). */
const loadConsumerProfessionals = async (args: LoaderFunctionArgs) => {
    const { request, context } = args;

    const redirectOpts = await checkTeamMemberInvitation(args);
    if (redirectOpts) {
        return redirect(redirectOpts.url, redirectOpts.init);
    }

    const { appDisplayMode, user, featureFlags } = context;
    const url = new URL(request.url);
    const isMobile = appDisplayMode === 'mobile';
    const { basePath, params } = parseURL(url.pathname);

    // We do not show product suppliers in the professionals directory page.
    if (basePath === '/professionals/product-supplier') {
        return redirect('/products/suppliers');
    }

    // Check location for urls /location-*
    let locationParams = {};
    let forceFilters = {};
    // Resolved location for category × location pages, kept here because the
    // `location-{slug}` path segment only yields a slug; we use the GraphQL-resolved
    // location to drive location-aware tiles and structured data. Set for both path
    // `location-{slug}` pages and `?location=` query filters.
    let appliedLocation: SearchLocation | undefined;
    // Slug from a path `location-{slug}` segment only. `parseURL` also fills
    // `params.location` from a `?location=` query filter, so we derive this from the
    // pathname to keep the geographic breadcrumb, hub treatment, mesh blocks,
    // location-aware titles and thin-page noindex limited to true location pages.
    const hasPathLocation = url.pathname
        .split('/')
        .some((segment) => segment.startsWith('location-'));
    let locationSlug: string | undefined;
    if (params.location || url.searchParams.get('location')) {
        const locationString = (params.location?.toString() ??
            url.searchParams.get('location')) as string;
        locationSlug = hasPathLocation
            ? params.location?.toString()
            : undefined;
        const location = await getProfessionalsLocation(args, locationString);
        if (!location) {
            throw response404();
        }

        // Location filters canonically live in the path (`/location-{slug}`), where the
        // page can be indexed and sitemapped. Permanently redirect the legacy
        // `?location={slug}` query form so each page has exactly one URL.
        if (!hasPathLocation) {
            return redirect(
                buildLocationPathRedirect(
                    basePath,
                    location.Slug ?? locationString,
                    url.searchParams,
                ),
                301,
            );
        }

        locationParams = buildLocationOverrideParams(location);
        appliedLocation = {
            suburb: location.Suburb ?? undefined,
            district: location.District ?? undefined,
            city: location.City ?? undefined,
            region: location.Region ?? undefined,
            state: location.State ?? undefined,
            country: location.Country ?? undefined,
            coordinate: toSearchCoordinate(
                location.Latitude,
                location.Longitude,
            ),
        };
        forceFilters = {
            Location: {
                Suburb: location.Suburb,
                District: location.District,
                City: location.City,
                Region: location.Region,
                State: location.State,
                Country: location.Country,
                // Lets the backend IsIndexableFilterPage verdict match the combo
                // registry to exactly one place. Ignored by content localisation.
                Slug: location.Slug ?? locationString,
            },
        };
    }

    const session = await getSession(request);
    const searchParams = url.searchParams;

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${searchParams.toString()}`,
            'professional',
            'directory',
            {
                pageSize,
                // url parameter
                ...locationParams,
            },
            DEFAULT_SORT,
            {
                // Exclude product suppliers from the professional directory page.
                excludeProductSuppliers: true,
                // A resolved location page also sends the place's coordinate
                // for distance ranking. The URL params carry the resolved name
                // levels, but never a coordinate.
                ...(appliedLocation && { searchLocation: appliedLocation }),
            },
        );
        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });
        const searchPromise = querySearchServiceSearch({
            context,
            queryParams,
        });
        const aggregationPromise = querySearchServiceAggregate({
            context,
            queryParams: aggregationProps,
        });

        return Promise.all([
            queryParams,
            aggregationProps,
            searchPromise,
            aggregationPromise,
        ]);
    };

    const sponsoredAdsPromise = adRequest({
        type: 'SponsorshipDirectoryPlacement',
        requestUrl: request.url,
        addPathContext: ['region'],
        ...args.context,
    });

    const [
        // Single combined call replaces the separate getDirectoryPageMeta + getPageLocalisation pair
        // (both previously hit the getDirectoryPageMeta resolver with identical variables).
        combinedPageMeta,
        // rootDirectoryPageMeta will be used as a fallback for missing category descriptions
        rootDirectoryPageMeta,
        professionalCategory,
        recentlyViewedProfessionalsResult,
        ads,
        sponsoredAds,
        esResults,
        activeDirectoryCategoriesCount,
        rootProfessionalCategoriesResult,
        categoriesInLocation,
    ] = await Promise.all([
        getDirectoryPageMetaCombined(args, {
            filters: forceFilters,
        }),
        // fetch the root professionals category meta to use its description as a fallback
        getDirectoryPageMeta({
            url: new URL('/professionals', request.url),
            ...context,
        }),
        getProfessionalsCategories({ args, ...args.context }),
        getRecentlyViewedServer({ type: 'Professionals' }, args.context),
        adsRequest({
            type: 'DirectoryWithProjectPlacementGroup',
            requestUrl: request.url,
            addPathContext: ['region'],
            ...args.context,
        }),
        sponsoredAdsPromise,
        // Waterfall call to query Sponsored Ads and professionals.
        // We need to wait for Sponsored Ads to be able to determine how many professionals we should query for the current page.
        (async () => {
            const sponsoredAdsResult = await sponsoredAdsPromise;
            return queryES(
                sponsoredAdsResult && sponsoredAdsResult.length > 0
                    ? DEFAULT_PROFESSIONAL_PAGE_SIZE -
                          Math.min(
                              sponsoredAdsResult.length,
                              MAX_SPONSORED_AD_TILES_DISPLAYED,
                          )
                    : DEFAULT_PROFESSIONAL_PAGE_SIZE,
            );
        })(),
        getActiveDirectoryCategoriesCount({}, args.context),
        // Root category tree for contextual bottom-of-page internal links (SSR on all viewports).
        getProfessionalCategoriesRocco(
            {
                urlInfo: parseURLRocco('/professionals'),
                excludeNonServiceCategories: true,
            },
            args.context,
        ),
        // Only path-based `location-{slug}` pages get the categories-in-place mesh
        // block (hub: browse categories here; category × location: sibling categories).
        locationSlug
            ? getProfessionalCategoriesInLocation(
                  { context, request },
                  locationSlug,
              )
            : Promise.resolve([]),
    ]);

    const { directoryPageMeta, localisation } = combinedPageMeta;

    const [queryParams, aggregationProps, searchResult, aggregations] =
        esResults;

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: DEFAULT_PROFESSIONAL_PAGE_SIZE,
    });

    const adsShuffled = ads?.CategoryPlacement
        ? shuffleArray(ads.CategoryPlacement)
        : [];

    const sponsoredAdsShuffled = sponsoredAds
        ? shuffleArray(sponsoredAds).slice(0, MAX_SPONSORED_AD_TILES_DISPLAYED)
        : [];

    // Calculate filter count early so we can determine if tiles should be eager-loaded
    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps.aggregations,
              searchType: 'professional',
              visibilityType: 'directory',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: queryParams.sortBy ?? null,
              defaultSortBy: DEFAULT_SORT,
          })
        : createEmptyFilterStates({
              visibilityType: 'directory',
              defaultSortBy: DEFAULT_SORT,
          });

    // We want to show ads even though the location filter is applied.
    const appliedFilterCount = getAppliedFilterNumber(filterStates, [
        'searchLocation',
    ]);

    // Determine if preloading is enabled (no ads/banners at top of grid)
    // We use the same condition for setting eager loading on tiles
    const shouldPreloadImages =
        appliedFilterCount > 0 ||
        (!adsShuffled.length && !sponsoredAdsShuffled.length);
    const numberTilesToEagerLoad = !isMobile ? 3 : 1;

    // US-only (archiproamerica.com): on category x location pages the tile label
    // reflects the page's target location instead of a non-matching head office.
    const professionals = getProfessionalsFromESSearchResults(
        searchResult,
        appliedLocation,
        context.country === 'US',
    );
    const tiles: TileViewModel<ProfessionalTileViewModelRocco>[] =
        professionals.map((professional, index) => ({
            aspectRatio: 'default',
            model: professional,
            // Set eager loading for first N tiles to match preloading
            eager: shouldPreloadImages && index < numberTilesToEagerLoad,
        }));

    const professionalTiles =
        searchResult?.hits?.reduce((tiles, hit) => {
            if (hit.PType === 'Professional')
                tiles.push(
                    mapESProfessionalTileViewModel(
                        hit,
                        'medium',
                        isMobile,
                        user,
                    ),
                );
            return tiles;
        }, [] as ProfessionalTileViewModel[]) ?? [];

    const sponsoredAdsTiles = sponsoredAdsShuffled.flatMap((advert) => {
        const professionalTile = advertCreativeProfessionalTileView(
            advert,
            'medium',
        );
        return professionalTile !== null ? professionalTile : [];
    });

    const projectAdsTiles = ads ? getProjectPlacements(ads, isMobile) : [];

    const enableAwardBadges = hasFeature(featureFlags, 'award_badges');
    const searchIslandPlaceholders = mapCategoryCountToPlaceholder(
        activeDirectoryCategoriesCount?.professionals ?? [],
        `Search professionals`,
        'professionals',
    );

    // Augment the loader data with the location-mesh fields (location breadcrumb,
    // hub flag, place name, and sibling categories with the self-link removed).
    const {
        directoryPageMeta: directoryPageMetaWithLocation,
        locationHub,
        appliedLocationName,
        categoriesInLocation: siblingCategoriesInLocation,
        categoryLocations,
    } = buildLocationMeshData({
        directoryPageMeta,
        appliedLocation,
        basePath,
        locationSlug,
        currentPath: url.pathname,
        categoriesInLocation,
        categoryLocations: professionalCategory.locations ?? [],
    });

    const rootCategories = mapCategoriesToTileViewModel(
        rootProfessionalCategoriesResult?.Categories?.edges ?? [],
    );
    const { categories: exploreCategories, title: exploreCategoriesTitle } =
        buildContextualProfessionalCategories({
            rootCategories,
            basePath,
            isRoot: !!directoryPageMetaWithLocation.isRoot,
        });
    const parsedFooterContent = parseFooterContentToFAQ(
        directoryPageMetaWithLocation.footerContent,
    );

    // Rocco filter states for the shared carousel-browse pill bar. Aggregations
    // (extra ES cost) are only fetched when the browse revamp is active; the
    // existing directory path pays nothing beyond the cheap URL-only parse.
    return {
        userPreferenceSession: session.getUserPreferences(),
        appDisplayMode,
        directoryPageMeta: directoryPageMetaWithLocation,
        locationHub,
        // True only on path-based `location-{slug}` pages; gates location-aware
        // titles and thin-page noindex so `?location=` query filters are excluded.
        isLocationPage: !!locationSlug,
        categoriesInLocation: siblingCategoriesInLocation,
        categoryLocations,
        appliedLocationName,
        searchResult,
        appliedLocation,
        professionalCategory,
        professionalTiles,
        professionalTilesRocco: tiles,
        pagination,
        recentlyViewedItems:
            recentlyViewedProfessionalsResult?.RecentlyViewedProfessionals.map(
                (professional) =>
                    mapProfessionalToTileViewModel(professional, {
                        appDisplayMode,
                    }),
            ) ?? [],
        // If directory doesn't have a description, use the root category description as a fallback
        categoryDescription:
            directoryPageMeta?.directoryHeaderDescription ||
            rootDirectoryPageMeta?.directoryHeaderDescription ||
            "ArchiPro features New Zealand's best professionals and contractors so you can find the help you need for your next building, renovation or redecoration project. We've selected the most trusted and reputable professionals offering quality products and the highest levels of service. Browse their work, discover their services and enquire directly from ArchiPro.",
        filterStates,
        ads: adsShuffled,
        sponsoredAds: sponsoredAdsTiles,
        projectAds: projectAdsTiles,
        localisation,
        url: url.pathname + url.search,
        appliedFilterCount,
        enableAwardBadges,
        searchIslandPlaceholders,
        rootCategories,
        exploreCategories,
        exploreCategoriesTitle,
        parsedFooterContent,
    };
};

export const shouldRevalidate = shouldRevalidateLeaf;

export const loader = async (args: LoaderFunctionArgs) => {
    // Business subdomain serves the B2B "Become a Pro" page at /professionals.
    // Branch before any consumer directory data fetching. Sub-paths
    // (e.g. /professionals/architects) have no B2B equivalent, so 404. The B2B
    // payload is cast to the consumer data type so this route's `typeof loader`
    // stays consumer-shaped for the directory component / SEO schema that read
    // it; the B2B branch is recovered at runtime via the `b2b` marker.
    if (isBusinessHost(args.request)) {
        if (args.params['*']) {
            throw response404();
        }
        const partnerBanners = await getB2BPartnerBanners(
            args.context,
            collectPartnerUrlSegments(profsConfig),
        );
        return {
            ...buildB2BRootLoaderData(getHost(args.request)),
            partnerBanners,
        } as unknown as Awaited<ReturnType<typeof loadConsumerProfessionals>>;
    }

    // Browse revamp: skip the heavy legacy directory loader and serve the small,
    // self-contained browse payload. The browse loader is entity-generic, so
    // resolve the professionals location meta separately and merge it in — the
    // meta builder needs it for location-aware titles + thin-page noindex on
    // `location-{slug}` URLs. `resolveProfessionalsLocationMeta` also 301s the
    // legacy `?location=` query form to the canonical path.
    if (isCategoryRevampActive(args.context, args.request)) {
        // Resolve the location first (also 301s the legacy `?location=` form);
        // it's a cheap no-op for non-location URLs. The grid then filters by it
        // so `location-{slug}` pages show firms for that place.
        const locationMeta = await resolveProfessionalsLocationMeta(args);
        const url = new URL(args.request.url);
        const { basePath, params } = parseURL(url.pathname);
        // Path-based `location-{slug}` only (a `?location=` query is already 301'd
        // by resolveProfessionalsLocationMeta), matching the legacy loader.
        const hasPathLocation = url.pathname
            .split('/')
            .some((segment) => segment.startsWith('location-'));
        const locationSlug = hasPathLocation
            ? params.location?.toString()
            : undefined;

        // Fetch the browse payload alongside the legacy below-grid data
        // (professional category for its locations, recently viewed, root
        // category tree, and the categories-in-location mesh block).
        const [
            browseData,
            professionalCategory,
            recentlyViewedProfessionalsResult,
            rootProfessionalCategoriesResult,
            categoriesInLocation,
            getMatchedStyleImages,
        ] = await Promise.all([
            loadDirectoryBrowse({
                context: args.context,
                request: args.request,
                entityType: 'professionals',
                config: PROFESSIONAL_FILTERS_CONFIG,
                searchIndex: SearchIndexes.Professionals,
                eyebrow: 'Professionals Library',
                searchLocation: locationMeta.appliedLocation,
                localisationFilters: locationMeta.localisationFilters,
                rootPath: '/professionals',
            }),
            getProfessionalsCategories({ args, ...args.context }),
            getRecentlyViewedServer({ type: 'Professionals' }, args.context),
            getProfessionalCategoriesRocco(
                {
                    urlInfo: parseURLRocco('/professionals'),
                    excludeNonServiceCategories: true,
                },
                args.context,
            ),
            locationSlug
                ? getProfessionalCategoriesInLocation(
                      { context: args.context, request: args.request },
                      locationSlug,
                  )
                : Promise.resolve([]),
            fetchGetMatchedStyleImages(args.context),
        ]);

        // Location-mesh fields (location breadcrumb, hub flag, place name, and
        // sibling categories with the self-link removed).
        const {
            directoryPageMeta: directoryPageMetaWithLocation,
            locationHub,
            appliedLocationName,
            categoriesInLocation: siblingCategoriesInLocation,
            categoryLocations,
        } = buildLocationMeshData({
            directoryPageMeta: browseData.directoryPageMeta,
            appliedLocation: locationMeta.appliedLocation,
            basePath,
            locationSlug,
            currentPath: url.pathname,
            categoriesInLocation,
            categoryLocations: professionalCategory.locations ?? [],
        });

        const rootCategories = mapCategoriesToTileViewModel(
            rootProfessionalCategoriesResult?.Categories?.edges ?? [],
        );
        const { categories: exploreCategories, title: exploreCategoriesTitle } =
            buildContextualProfessionalCategories({
                rootCategories,
                basePath,
                isRoot: !!directoryPageMetaWithLocation.isRoot,
            });

        return {
            ...browseData,
            ...locationMeta,
            directoryPageMeta: directoryPageMetaWithLocation,
            recentlyViewedItems:
                recentlyViewedProfessionalsResult?.RecentlyViewedProfessionals.map(
                    (professional) =>
                        mapProfessionalToTileViewModel(professional, {
                            appDisplayMode: args.context.appDisplayMode,
                        }),
                ) ?? [],
            exploreCategories,
            exploreCategoriesTitle,
            parsedFooterContent: parseFooterContentToFAQ(
                directoryPageMetaWithLocation.footerContent ?? '',
            ),
            locationHub,
            appliedLocationName,
            categoriesInLocation: siblingCategoriesInLocation,
            categoryLocations,
            isLocationPage: locationMeta.isLocationPage,
            professionalCategoryTitle: professionalCategory.title,
            getMatchedStyleImages,
        } as unknown as Awaited<ReturnType<typeof loadConsumerProfessionals>>;
    }

    return loadConsumerProfessionals(args);
};

const ProfessionalCategoryPage = () => {
    const user = useUser();
    const { setCategory } = useCurrentCategory();
    const { css } = useStyles();
    const {
        professionalCategory,
        categoryDescription,
        directoryPageMeta,
        professionalTiles,
        recentlyViewedItems,
        filterStates,
        ads,
        sponsoredAds,
        projectAds,
        appliedFilterCount,
        professionalTilesRocco,
        pagination,
        exploreCategories,
        exploreCategoriesTitle,
        parsedFooterContent,
        locationHub,
        categoriesInLocation,
        categoryLocations,
        appliedLocationName,
        isLocationPage,
    } = useLoaderData<typeof loader>();

    const appDisplayMode = useAppDisplayModeContext();
    const { desktop, mobile } = appDisplayMode;
    const tracker = useTracker();
    const [searchParams] = useSearchParams();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(ads, ['region']);
    const sponsoredAdsMemoized = useLocationMemo(sponsoredAds, ['region']);
    const projectAdsMemoized = useLocationMemo(projectAds, ['region']);
    const professionalTilesRoccoAdmin = useTilesAdminData(
        professionalTilesRocco as TileViewModel<ProfessionalTileViewModelRocco>[],
    );
    const { isInDesignBoard } = useHandleFollow();

    // init category tiles tracking
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const remixLocation = useLocation();
    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedProfessions = searchParams
        .getAll('professionals')
        .join(' | ');
    const selectedRegion = searchParams.getAll('region').join(' | ');

    useEffect(() => {
        tracker.log('professionalListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                professionals: selectedProfessions,
                region: selectedRegion,
            },
        });
    }, [selectedCategory, selectedProfessions, selectedRegion, tracker]);

    useEffect(() => {
        setCategory('Professionals');
        return () => setCategory(null);
    }, [setCategory]);

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();
    const gridColumns = desktop ? (filterDrawerOpen ? 3 : 4) : 1;

    const pageTitle = buildProfessionalDirectoryDisplayTitle({
        directoryHeaderTitle: directoryPageMeta.directoryHeaderTitle,
        categoryTitle: professionalCategory.title,
        appliedLocationName,
        isLocationPage,
        locationHub,
    });

    const professionalIDs = professionalTiles.map(
        (professional) => professional.professionalID,
    );
    const sponsoredAdsIDs = sponsoredAdsMemoized.map(
        (professional) => professional.professionalID,
    );
    const { getTileTopActions, hotButtonModal, getTileAdminCaption } =
        useTileHotButtons({
            itemType: 'professional',
            itemIDs: [...professionalIDs, ...sponsoredAdsIDs],
        });

    const tiles = professionalTilesRoccoAdmin.map((tile) => (
        <CategoryPageTileWithAdminActions key={tile.model.id} tile={tile} />
    ));

    const searchFilters = useSearchFilters({
        category: 'professional',
        initialStates: filterStates,
        isCategoryPage: true,
    });

    if (!appliedFilterCount && sponsoredAdsMemoized.length > 0) {
        sponsoredAdsMemoized.forEach((professionalTile, index) => {
            if (!professionalTile.advertCreative) {
                return;
            }

            const tile = professionalTile.tile;
            const link = professionalTile.advertCreative.cta.href;
            const professionalID = professionalTile.professionalID;

            const imageProps = tile.image.map((image) => {
                return {
                    src: image,
                    width: '100%',
                    height: '100%',
                    sizes: getImagesSizes(595),
                    alt: tile.title,
                };
            });

            const topActions = getTileTopActions({ item: tile });

            const adminCaption = getTileAdminCaption(professionalID);

            const {
                canEdit: _canEdit,
                bmEditorLink: _bmEditorLink,
                ...rest
            } = tile;

            tiles.unshift(
                <FollowSelectorProxy
                    key={`sponsored_${link}`}
                    pinId={professionalID}
                    pinType="ProfessionalPin"
                >
                    {({ openFollowSelector }) => (
                        <ProfessionalAdvertTile
                            tag={'Sponsored'}
                            advertCreative={professionalTile.advertCreative!}
                            prefetch="intent"
                            {...rest}
                            image={imageProps}
                            impression={{
                                professionalID: professionalID,
                                itemID: professionalID,
                                type: 'LibraryItem_Directory',
                            }}
                            logo={{
                                image: {
                                    src:
                                        typeof tile.logo === 'object'
                                            ? ((
                                                  tile.logo as unknown as AvatarProps
                                              ).image as string)
                                            : '',
                                    alt: `${tile.title} professional logo`,
                                    loading:
                                        index < gridColumns ? 'eager' : 'lazy',
                                },
                                variables: (tile.logo as AvatarProps).variables,
                            }}
                            size={
                                !desktop
                                    ? 'small'
                                    : desktop && !filterDrawerOpen
                                      ? 'large'
                                      : 'medium'
                            }
                            variables={{
                                ...(!desktop && {
                                    dynamicSizingWidth: 414,
                                    dynamicSizingGapWidth: 0,
                                    tilePadding: 0,
                                    avatarMargin: 18,
                                }),
                            }}
                            variant={'04'}
                            onFavourite={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openFollowSelector(e);
                            }}
                            isFavourited={isInDesignBoard(
                                professionalID,
                                'ProfessionalPin',
                            )}
                            services={tile.services.slice(0, 3)}
                            onClick={() => {
                                logDirectoryItemClick(tracker, {
                                    itemID: professionalID,
                                    offset: index,
                                    categoryType: 'professionals',
                                    appDisplayMode,
                                });
                            }}
                            topActions={topActions}
                            adminCaption={adminCaption}
                        />
                    )}
                </FollowSelectorProxy>,
            );
        });
    }

    // Insert a project ad every two rows. at most 3 project ads.
    if (projectAdsMemoized.length) {
        const adsSlotGap = mobile ? ADS_SLOT_GAP_MOBILE : gridColumns * 2;
        const slotCount = Math.min(MAX_ADS_SLOT, tiles.length / adsSlotGap);
        const adsCount = Math.min(MAX_ADS_SLOT, projectAdsMemoized.length);
        let slotIndex = 0;
        let adIndex = 0;
        while (slotIndex < slotCount && adIndex < adsCount) {
            const ad = projectAdsMemoized[adIndex];
            if (!ad) {
                break;
            }
            tiles.splice(
                (slotIndex + 1) * adsSlotGap + slotIndex,
                0,
                getProjectPlacementAdsTile(ad, gridColumns),
            );
            slotIndex++;
            adIndex++;
        }
    }

    if (appliedFilterCount === 0 && adsMemoized.length && adsMemoized[0]) {
        tiles.unshift(
            <AdvertBannerTile
                advertCreative={adsMemoized[0]}
                columns={gridColumns}
                desktop={desktop}
                key="AdvertBannerTile"
            />,
        );
    }

    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const apConnectBannerFlag = useFeature('archipro_connect_category_banner');

    const showProfessionalArchiproBanner =
        getShowProfessionalArchiproConnectBanner(user);

    if (showProfessionalArchiproBanner && apConnectBannerFlag) {
        tiles.unshift(
            <div
                className={css(S.APConnectBannerCell)}
                key="APConnectBannerCell"
            >
                <APConnectBanner />
            </div>,
        );
    }

    return (
        <Box>
            <Container>
                {desktop ? (
                    <TitleSectionDesktop
                        category={pageTitle}
                        rootCategory="Professionals."
                        categoryDescription={categoryDescription}
                        itemsInCategory={filterStates.totalCount}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        isRoot={directoryPageMeta.isRoot}
                    />
                ) : (
                    <TitleSectionMobile
                        title={pageTitle}
                        categoryDescription={categoryDescription}
                        subtitle={''}
                    >
                        <TitleSectionFilterMobile
                            esCategory="professional"
                            searchFilters={searchFilters}
                        />
                    </TitleSectionMobile>
                )}

                {desktop && (
                    <CategoryFilterSection
                        category="professional"
                        searchFilters={searchFilters}
                        filterDrawerOpen={filterDrawerOpen}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        componentBefore={
                            <CollapsibleToggle
                                filterDrawerOpen={filterDrawerOpen}
                                toggleFilterDrawer={toggleFilterDrawer}
                            />
                        }
                    />
                )}
                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <CategoryFilterNav
                                category="professional"
                                searchFilters={searchFilters}
                                hideCategorySectionTitle={true}
                                hideCategoryBackButton={false}
                            />
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div>
                        {desktop &&
                            searchFilters.appliedFilters &&
                            searchFilters.appliedFilters.length > 0 && (
                                <AppliedFilters
                                    filterItems={searchFilters.appliedFilters}
                                    resetFilters={() =>
                                        searchFilters.resetFilters(
                                            resetFilterStates(
                                                searchFilters.filterStates,
                                                ['category'],
                                            ),
                                        )
                                    }
                                />
                            )}

                        {tiles.length ? (
                            <Grid
                                ref={gridRef}
                                colClasses={
                                    filterDrawerOpen
                                        ? 'grid-cols-1 md:grid-cols-3 qhd:grid-cols-4'
                                        : 'grid-cols-1 md:grid-cols-4'
                                }
                                rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                            >
                                {tiles}
                            </Grid>
                        ) : (
                            <NoResultsSection resultsType={'professionals'} />
                        )}
                        {pagination.totalPages > 1 && (
                            <div className="pt-26.25 md:pt-20">
                                <TilePagination
                                    currentPath={remixLocation}
                                    current={pagination.page}
                                    total={pagination.totalPages}
                                    urlBuilder={buildPaginationUrl}
                                    visibleCount={
                                        appDisplayMode.desktop ? 4 : 3
                                    }
                                />
                            </div>
                        )}
                    </div>
                </CollapsiblePageLayout>

                <div className="mt-20 mb-10 flex flex-col gap-10 md:mt-[210px] md:mb-20 md:gap-17.5">
                    <div className="flex flex-col">
                        <CategoryPageFAQ
                            faqSchemas={directoryPageMeta.faqSchemas}
                            footerContent={directoryPageMeta.footerContent}
                            parsedFooterContent={parsedFooterContent}
                            variant={desktop ? 'grid' : undefined}
                            seoHeading={
                                isLocationPage && appliedLocationName
                                    ? `Learn about ${professionalCategory.title} in ${appliedLocationName}`
                                    : directoryPageMeta.isRoot
                                      ? undefined
                                      : `Learn about ${directoryPageMeta.directoryHeaderTitle}`
                            }
                        />

                        <CategoryPageLocations
                            category={professionalCategory.title}
                            locations={categoryLocations}
                            isHub={locationHub}
                            continuesFromPreviousSection
                        />

                        <CategoryPageCategoriesInLocation
                            locationName={appliedLocationName}
                            categories={categoriesInLocation}
                            stackedAfterMeshSection
                        />

                        <ProfessionalCategoryExploreSection
                            categories={exploreCategories}
                            title={exploreCategoriesTitle}
                            isRoot={!!directoryPageMeta.isRoot}
                        />
                    </div>

                    <CategoryPageRecentlyViewed
                        displayMode={
                            appDisplayMode.desktop ? 'desktop' : 'mobile'
                        }
                        items={recentlyViewedItems}
                    />
                </div>

                {hotButtonModal}
            </Container>
        </Box>
    );
};

const getProjectPlacementAdsTile = (ad: ProjectAd, gridColumns: number) => {
    return (
        <AdvertProjectPlacementTile
            key={`project-placement-${ad.id}`}
            ad={ad}
            columns={gridColumns}
        />
    );
};

/** Browse loader payload + the below-grid content ported from legacy professionals. */
type ProfessionalsBrowseLoaderData = DirectoryBrowseLoaderData & {
    recentlyViewedItems: ReturnType<typeof mapProfessionalToTileViewModel>[];
    exploreCategories: ReturnType<
        typeof buildContextualProfessionalCategories
    >['categories'];
    exploreCategoriesTitle: string;
    parsedFooterContent: ReturnType<typeof parseFooterContentToFAQ>;
    locationHub: boolean;
    appliedLocationName: ComponentProps<
        typeof CategoryPageCategoriesInLocation
    >['locationName'];
    categoriesInLocation: ComponentProps<
        typeof CategoryPageCategoriesInLocation
    >['categories'];
    categoryLocations: ComponentProps<
        typeof CategoryPageLocations
    >['locations'];
    isLocationPage: boolean;
    professionalCategoryTitle: string;
    getMatchedStyleImages: GetMatchedStyleImage[];
};

/**
 * Professionals carousel browse page (behind the category_pages_revamp flag).
 * Builds the shared filter bar from the professionals filter config + the
 * loader's Rocco filter states, then renders it in DirectoryBrowsePage.
 */
const ProfessionalsDirectoryBrowse = () => {
    const data = useLoaderData<
        typeof loader
    >() as unknown as ProfessionalsBrowseLoaderData;
    const { desktop } = useAppDisplayModeContext();
    const filterSortProps = useCategoryFilterSort(
        PROFESSIONAL_FILTERS_CONFIG,
        SearchIndexes.Professionals,
        data.filterStates,
    );
    // Display dropdown: user-selected desktop grid columns for this vertical.
    const { columns: displayColumns, setColumns: setDisplayColumns } =
        useDirectoryDisplayColumns('professionals', 4);
    const { browse } = data;
    // Root vs category: gate on breadcrumb depth (root has a single crumb).
    const activeCategory =
        data.breadcrumbs.length > 1
            ? data.breadcrumbs[data.breadcrumbs.length - 1]?.label
            : undefined;
    const sectionTitle = activeCategory
        ? `All ${activeCategory} Professionals`
        : 'All Professionals';
    const childPills = browse.categoryCards.map((category) => ({
        id: category.id,
        title: category.title,
        link: category.link,
    }));
    const categoryPills = activeCategory
        ? childPills
        : [
              { id: 0, title: 'All Professionals', link: '/professionals' },
              ...childPills,
          ];
    return (
        <>
            {/* Listener for the intro / grid-injection Get Matched buttons
                (shared jotai atom). Ships with the revamp flag that gates this
                whole component — no separate Get Matched flag. From
                /professionals the audience is known, so the type-selector
                step is skipped via defaultEnquiryType. */}
            <GetMatchedModal
                backgroundImage={null}
                defaultEnquiryType="professional"
                styleImages={data.getMatchedStyleImages}
            />
            <DirectoryBrowsePage
                entityType="professionals"
                intro={
                    <DirectoryIntro
                        eyebrow={data.eyebrow}
                        heading={data.heading}
                        categoryPrefix={activeCategory}
                    />
                }
                categoryCarousel={
                    !activeCategory && browse.categoryCards.length > 0 ? (
                        <DirectoryCategoryCarousel
                            title="Explore Professional Categories"
                            categories={browse.categoryCards}
                            countLabel="Firms"
                        />
                    ) : null
                }
                pillBar={
                    filterSortProps?.searchFilters ? (
                        <DirectoryPillBar
                            breadcrumbs={data.breadcrumbs}
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            prefetchUrl={filterSortProps.settledDestinationURL}
                            sectionTitle={sectionTitle}
                            categoryPills={categoryPills}
                            pillsScopedToMobile={browse.pillsAreSiblings}
                            rootPath="/professionals"
                            rootLabel="All Professionals"
                            displayColumns={displayColumns}
                            onDisplayColumnsChange={setDisplayColumns}
                        />
                    ) : null
                }
                sidebar={
                    filterSortProps?.searchFilters ? (
                        <DirectoryFilterSidebar
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            categories={childPills}
                        />
                    ) : null
                }
                rows={
                    <DirectoryBrowseGrid
                        entityType="professionals"
                        columns={displayColumns}
                        entityNoun="professionals"
                        aspectRatio="portrait"
                        categoryLink={browse.categoryLink}
                        initialTiles={browse.gridTiles}
                        totalCount={browse.gridTotalCount}
                        filters={browse.filters}
                        sortBy={browse.sortBy ?? undefined}
                        firstLoadedPage={browse.firstLoadedPage}
                        requestedPage={browse.requestedPage}
                        activeCategoryLabel={activeCategory}
                    />
                }
                belowGrid={
                    // Legacy professionals below-grid: newspaper FAQ → locations mesh
                    // → categories-in-location → explore categories → recently viewed.
                    <div className="mt-20 mb-10 flex flex-col gap-10 md:mt-[210px] md:mb-20 md:gap-17.5">
                        <div className="flex flex-col">
                            <CategoryPageFAQ
                                faqSchemas={data.directoryPageMeta.faqSchemas}
                                footerContent={
                                    data.directoryPageMeta.footerContent
                                }
                                parsedFooterContent={data.parsedFooterContent}
                                variant={desktop ? 'grid' : undefined}
                                seoHeading={
                                    data.isLocationPage &&
                                    data.appliedLocationName
                                        ? `Learn about ${data.professionalCategoryTitle} in ${data.appliedLocationName}`
                                        : data.directoryPageMeta.isRoot
                                          ? undefined
                                          : `Learn about ${data.directoryPageMeta.directoryHeaderTitle}`
                                }
                            />

                            <CategoryPageLocations
                                category={data.professionalCategoryTitle}
                                locations={data.categoryLocations}
                                isHub={data.locationHub}
                                continuesFromPreviousSection
                            />

                            <CategoryPageCategoriesInLocation
                                locationName={data.appliedLocationName}
                                categories={data.categoriesInLocation}
                                stackedAfterMeshSection
                            />

                            <ProfessionalCategoryExploreSection
                                categories={data.exploreCategories}
                                title={data.exploreCategoriesTitle}
                                isRoot={!!data.directoryPageMeta.isRoot}
                            />
                        </div>

                        <CategoryPageRecentlyViewed
                            displayMode={desktop ? 'desktop' : 'mobile'}
                            items={data.recentlyViewedItems}
                        />
                    </div>
                }
            />
        </>
    );
};

const ProfessionalsRoute = () => {
    const data = useLoaderData<typeof loader>();
    const categoryRevamp = useCategoryRevamp();
    if (isB2BRootLoaderData(data)) {
        return (
            <B2BLandingPage
                config={profsConfig}
                mainSiteOrigin={data.mainSiteOrigin}
                partnerBanners={data.partnerBanners}
            />
        );
    }
    if (categoryRevamp) {
        return <ProfessionalsDirectoryBrowse />;
    }
    return <ProfessionalCategoryPage />;
};

/**
 * Category surfaces opt their tiles into the listing→detail view transitions
 * (BC-4786): everything on this route sits inside the scope, so grid tiles,
 * sliders and recently-viewed strips all morph/sheet, while the same tile
 * components rendered on detail pages or the homepage navigate plainly.
 */
const ProfessionalsRouteWithTileTransitions = () => (
    <TileViewTransitionScope>
        <ProfessionalsRoute />
    </TileViewTransitionScope>
);

export default ProfessionalsRouteWithTileTransitions;
